import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { InputField, Checkbox } from '@rocketlane/ui';
import { GenerateAsOption, GroupByOption } from 'api/custom-section/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SignalSelector } from './signal-selector';
import { useAutoFocusOnTitle } from '../../../shared/auto-focus-title';
import { SharedConfigurationBar } from '../../../shared/configuration-bar';
import {
  SectionCard,
  SectionDivider,
  TextAreaContainer,
} from '../../../shared/section-styles';
import { useSourcesUpdaterState } from '../../people-page-section-sources';
import { PeoplePageDerivedSectionSources } from '../../people-page-section-sources';
import {
  Configuration,
  defaultConfiguration,
  PeoplePageSourceDerivedSignalSectionComponentProps,
  PromptInputFormValues,
} from '../types';
import { attributesToForm } from '../utils/attributes-to-form';
import { formValuesIntoAttributes } from '../utils/form-to-attributes';

const NoPaddingFormItem = styled(Form.Item)`
  padding: 0 !important;
`;
const TextAreaFormItem = styled(Form.Item)`
  margin-top: calc(var(--spacing-05) * -1) !important;
  ${TextAreaContainer} {
    margin-top: 0 !important;
  }
`;

const StyledSectionCard = styled(SectionCard)`
  padding: var(--spacing-05) var(--spacing-05);
`;

interface Props extends PeoplePageSourceDerivedSignalSectionComponentProps {
  onEditDone: () => void;
}
export const PeoplePageSourceDerivedSignalSectionPromptInput: FC<Props> = (
  props,
) => {
  const {
    node,
    allowResolve,
    onDone,
    onEditDone,
    pageType,
    pageTriggerListFilter,
  } = props;
  const titleRef = useAutoFocusOnTitle();
  const titlePlaceholder = useTranslation(
    'rich_text_editor.sections.question_section.title_placeholder',
  );
  const aiContextPlaceholder = useTranslation(
    'rich_text_editor.sections.section.context_placeholder',
  );
  const addContextLabel = useTranslation(
    'rich_text_editor.sections.question_section.add_context',
  );
  const [form] = Form.useForm<PromptInputFormValues>();
  const [hasError, setHasError] = useState(false);
  const [configuration, setConfiguration] =
    useState<Configuration>(defaultConfiguration);
  const onValuesChange = useCallback(() => {
    queueMacrotask(() => {
      form
        .validateFields()
        .then((values) => {
          setHasError(false);
          setConfiguration({
            groupBy: values.groupBy,
            generateAs: values.generateAs,
            sourcePageTemplates: values.sourcePageTemplates,
          });
        })
        .catch(() => {
          setHasError(true);
        });
    });
  }, [form]);

  useEffect(() => {
    form.setFieldsValue(attributesToForm(node.attrs));
    onValuesChange();
  }, [form, node.attrs, onValuesChange]);
  const onConfigurationChange = useCallback(
    (changedValues: Partial<Configuration>) => {
      form.setFieldsValue({
        groupBy: changedValues.groupBy,
        generateAs: changedValues.generateAs,
        sourcePageTemplates: changedValues.sourcePageTemplates,
      });
      setConfiguration((previous) => ({
        ...previous,
        ...changedValues,
      }));
      onValuesChange();
    },
    [form, onValuesChange],
  );
  const {
    hasSourcePageTemplateSectionsUpdater,
    invokeSourcePageTemplateSectionsUpdater,
    sourcesUpdateInProgress,
    closePendingUpdater,
  } = useSourcesUpdaterState({ ...props, onConfigurationChange });

  const [actionLoading, setActionLoading] = useState(false);
  const onSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      setActionLoading(true);
      closePendingUpdater();
      onDone(formValuesIntoAttributes(values, node.attrs)).then(() => {
        setActionLoading(false);
        onEditDone();
      });
    });
  }, [closePendingUpdater, form, node.attrs, onDone, onEditDone]);
  const onRemove = useCallback(() => {
    closePendingUpdater();
    props.onRemove();
  }, [closePendingUpdater, props]);

  return (
    <StyledSectionCard $active={sourcesUpdateInProgress}>
      <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
        <Form.Item
          name="title"
          label={
            <FormattedMessage id="rich_text_editor.sections.question_section.title" />
          }
          requiredMark={'optional'}
          rules={[
            {
              required: true,
              message: '',
            },
          ]}
          validateStatus={'success'}
        >
          <InputField
            type="text"
            ref={titleRef}
            placeholder={titlePlaceholder}
          />
        </Form.Item>
        <Form.Item
          name="signalConfigurations"
          label={
            <FormattedMessage id="rich_text_editor.sections.people_page_source_derived_signal_section.fields.signal_configurations.label" />
          }
          requiredMark={'optional'}
          rules={[
            {
              required: true,
              message: '',
            },
          ]}
          validateStatus={'success'}
        >
          <SignalSelector />
        </Form.Item>
        <Form.Item
          shouldUpdate={(prev, curr) =>
            prev.aiContextChecked !== curr.aiContextChecked
          }
        >
          {() => (
            <Checkbox
              color="black"
              label={addContextLabel}
              checked={form.getFieldValue('aiContextChecked')}
              onCheckedChange={(checked) => {
                form.setFieldsValue({ aiContextChecked: checked as boolean });
              }}
            />
          )}
        </Form.Item>
        <NoPaddingFormItem shouldUpdate>
          {({ getFieldValue }) => {
            const aiContextChecked = getFieldValue('aiContextChecked');

            return (
              <TextAreaFormItem name="aiContext" hidden={!aiContextChecked}>
                <TextAreaContainer placeholder={aiContextPlaceholder} />
              </TextAreaFormItem>
            );
          }}
        </NoPaddingFormItem>
        <Form.Item name="groupBy" hidden />
        <Form.Item name="generateAs" hidden />
        <Form.Item name="sourcePageTemplates" hidden />
      </Form>
      <SharedConfigurationBar
        prompt={configuration}
        setPrompt={onConfigurationChange}
        isActionLoading={actionLoading}
        onCancel={onRemove}
        onSubmit={onSubmit}
        validation={{
          isDisabled: hasError,
        }}
        allowResolve={allowResolve}
        defaultValues={{
          groupBy: GroupByOption.Topics,
          generateAs: GenerateAsOption.Bullet,
        }}
        options={{
          groupBy: Object.values(GroupByOption),
          generateAs: Object.values(GenerateAsOption),
        }}
        pageType={pageType}
        pageTriggerListFilter={pageTriggerListFilter}
      />
      <SectionDivider />
      <PeoplePageDerivedSectionSources
        sources={configuration.sourcePageTemplates}
        onClick={
          hasSourcePageTemplateSectionsUpdater
            ? () =>
                invokeSourcePageTemplateSectionsUpdater({
                  ...node,
                  attrs: hasError
                    ? {
                        ...node.attrs,
                        config: {
                          ...node.attrs.config,
                          sourcePageTemplates:
                            configuration.sourcePageTemplates,
                        },
                      }
                    : formValuesIntoAttributes(
                        form.getFieldsValue(),
                        node.attrs,
                      ),
                })
            : undefined
        }
      />
    </StyledSectionCard>
  );
};
